import React from 'react';
import { ScreenName } from '@helpcenter/constants';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import * as grid from '@helpcenter/styles/src/grid';
import { sanitize } from 'dompurify';
import { isIsomorphicDompurifyLibUsed } from 'feature-flags';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery, graphql } from 'react-relay';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { isValidAnnouncement } from '@atlassian/help-center-common-util/login-announcements/login-announcements';
import type { loginAnnouncementsCardQuery } from './__generated__/loginAnnouncementsCardQuery.graphql';

// Since LoginAnnouncement epic is made for home page which get messages as plain text, we can't use this epic for our use case, hence we are using useLazyLoadQuery to get the data using graphql query
const LoginAnnouncement = () => {
    di(useLazyLoadQuery);
    const data = useLazyLoadQuery<loginAnnouncementsCardQuery>(
        graphql`
            query loginAnnouncementsCardQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: "HelpCenterAggBeta") {
                        ... on HelpCenter {
                            __typename
                            announcements {
                                loginAnnouncements {
                                    name
                                    description
                                    descriptionType
                                }
                            }
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
            }
        `,
        {
            helpCenterAri: getHelpCenterAri(),
            cloudId: getCloudId(),
        }
    );

    const responseTypeName = data.helpCenter?.helpCenterById?.__typename;
    if (responseTypeName !== 'HelpCenter') {
        // not displaying anything if the response is not of type HelpCenter
        return null;
    }

    // As confirmed by the backend team, there will be only one login announcement for now, hence using the first element
    const announcementDetails = data.helpCenter?.helpCenterById?.announcements?.loginAnnouncements?.[0];
    const loginAnnouncementSubject = announcementDetails?.name ?? '';
    const loginAnnouncementMessage = announcementDetails?.description ?? '';
    const loginAnnouncementDescriptionType = announcementDetails?.descriptionType ?? '';
    const isAnnouncementValid = isValidAnnouncement(loginAnnouncementSubject, loginAnnouncementMessage);

    if (!isAnnouncementValid) {
        // not displaying anything if the announcement is not valid or the description type is not WIKI_MARKUP
        return null;
    }

    return (
        <Box xcss={loginAnnouncementContainerStyles}>
            {loginAnnouncementSubject ? <Heading level="h500">{loginAnnouncementSubject}</Heading> : null}
            {loginAnnouncementMessage && loginAnnouncementDescriptionType === 'WIKI_MARKUP' ? (
                <Box
                    xcss={loginAnnouncementBodyStyles}
                    dangerouslySetInnerHTML={{
                        __html: isIsomorphicDompurifyLibUsed()
                            ? isomorphicSanitize(loginAnnouncementMessage)
                            : sanitize(loginAnnouncementMessage),
                    }}
                />
            ) : null}
        </Box>
    );
};

const LoginAnnouncementLoader = () => {
    return (
        <ErrorBoundary packageName={ScreenName.LOGIN} id={'login-announcements'}>
            <LoginAnnouncement />
        </ErrorBoundary>
    );
};

export default LoginAnnouncementLoader;

const loginAnnouncementContainerStyles = xcss({
    marginTop: 'space.300',
    paddingBottom: 'space.200',
    borderBottom: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.14)')}`,
});

const loginAnnouncementBodyStyles = xcss({
    marginTop: 'space.100',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    maxWidth: grid.multiple(42).px,
    overflowWrap: 'break-word',
});
